import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import { createTopic, updateTopicDetails, deleteTopic } from "@/lib/api";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";

const BLANK = { name: "", description: "", est_hours: 2, difficulty: 3, assignment: "" };

export default function TopicDialog({ open, onOpenChange, topic, level, onSaved }) {
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const isEdit = Boolean(topic);

  useEffect(() => {
    if (open) {
      setForm(
        topic
          ? {
              name: topic.name,
              description: topic.description || "",
              est_hours: topic.est_hours,
              difficulty: topic.difficulty,
              assignment: topic.assignment || "",
            }
          : BLANK,
      );
    }
  }, [open, topic]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name.trim()) {
      toast.error("Name is required");
      return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        await updateTopicDetails(topic.id, form);
        toast.success("Topic updated");
      } else {
        await createTopic({ ...form, level });
        toast.success("Topic added");
      }
      onOpenChange(false);
      onSaved && onSaved();
    } catch {
      toast.error("Could not save topic");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    setSaving(true);
    try {
      await deleteTopic(topic.id);
      toast.success("Topic removed");
      onOpenChange(false);
      onSaved && onSaved();
    } catch {
      toast.error("Could not delete topic");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border text-foreground max-w-lg" data-testid="topic-dialog">
        <DialogHeader>
          <DialogTitle className="mono tracking-tight">
            $ {isEdit ? "edit topic" : "new topic"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 max-h-[65vh] overflow-y-auto pr-1">
          <div>
            <label className="overline block mb-1">Name</label>
            <Input
              data-testid="topic-name-input"
              value={form.name}
              onChange={(e) => update("name", e.target.value)}
              className="bg-muted border-border"
            />
          </div>
          <div>
            <label className="overline block mb-1">Description</label>
            <Textarea
              data-testid="topic-description-input"
              value={form.description}
              onChange={(e) => update("description", e.target.value)}
              className="bg-muted border-border resize-none"
              rows={2}
            />
          </div>
          <div>
            <label className="overline block mb-1">Assignment</label>
            <Textarea
              data-testid="topic-assignment-input"
              value={form.assignment}
              onChange={(e) => update("assignment", e.target.value)}
              className="bg-muted border-border resize-none"
              rows={2}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="overline block mb-1">Est. hours</label>
              <Input
                data-testid="topic-hours-input"
                type="number"
                step="0.5"
                min="0.5"
                value={form.est_hours}
                onChange={(e) => update("est_hours", Number(e.target.value))}
                className="bg-muted border-border mono"
              />
            </div>
            <div>
              <label className="overline block mb-1">Difficulty {form.difficulty}/5</label>
              <Slider
                value={[form.difficulty]}
                min={1}
                max={5}
                step={1}
                onValueChange={(v) => update("difficulty", v[0])}
                className="mt-3"
              />
            </div>
          </div>
        </div>
        <DialogFooter className="flex items-center sm:justify-between gap-2">
          {isEdit ? (
            <Button
              variant="outline"
              data-testid="topic-delete-btn"
              onClick={remove}
              disabled={saving}
              className="border-destructive text-destructive hover:bg-destructive hover:text-white mono text-xs"
            >
              <Trash2 className="h-4 w-4 mr-2" /> Delete
            </Button>
          ) : <span />}
          <Button
            data-testid="topic-save-btn"
            onClick={save}
            disabled={saving}
            className="bg-primary text-primary-foreground hover:bg-secondary hover:text-white mono uppercase tracking-widest text-xs"
          >
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
